import React, { createContext, useContext, useEffect, useState } from 'react';
import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendEmailVerification,
  updateEmail as firebaseUpdateEmail,
  User,
  sendPasswordResetEmail,
  verifyBeforeUpdateEmail,
  EmailAuthProvider,
  reauthenticateWithCredential
} from 'firebase/auth';
import { auth as importedAuth, db } from '../lib/firebase';
import { doc, setDoc, getDoc } from 'firebase/firestore';

const auth = importedAuth!;


interface AuthContextType {
  user: User | null;
  loading: boolean;
  isBanned: boolean;
  isAdmin: boolean;
  signup: (email: string, password: string, userId: string) => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updateEmail: (newEmail: string, password: string) => Promise<void>;
  verifyEmail: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isBanned, setIsBanned] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isClosed, setIsClosed] = useState(false);

  useEffect(() => {
    console.log('Setting up auth state listener...');
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log('Auth state changed:', user ? `User ${user.uid} logged in` : 'No user');
      
      if (user) {
        // Check if account is closed
        const closedAccounts = JSON.parse(localStorage.getItem('closedAccounts') || '[]');
        
        if (closedAccounts.includes(user.uid)) {
          console.log('Account is closed, signing out');
          setIsClosed(true);
          await signOut(auth);
          setUser(null);
          setIsAdmin(false);
          return;
        }

        // Check if user is banned and admin status
        try {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            
            if (userData.isBanned) {
              console.log('User is banned, signing out');
              setIsBanned(true);
              
              // Show alert before signing out
              alert('Your account has been suspended due to a violation of our community guidelines. If you believe this is an error, please contact our support team.');
              
              await signOut(auth);
              setUser(null);
              setIsAdmin(false);
            } else {
              setIsBanned(false);
              setUser(user);
              setIsAdmin(userData.isAdmin === true);
              console.log('User admin status:', userData.isAdmin === true);
            }
          } else {
            setIsBanned(false);
            setUser(user);
            setIsAdmin(false);
          }
        } catch (error) {
          console.error('Error checking user status:', error);
          setIsBanned(false);
          setUser(user);
          setIsAdmin(false);
        }
      } else {
        setUser(null);
        setIsBanned(false);
        setIsAdmin(false);
      }
      
      setLoading(false);
    });

    return () => {
      console.log('Cleaning up auth state listener');
      unsubscribe();
    };
  }, []);

  async function signup(email: string, password: string, userId: string) {
    try {
      console.log('Attempting signup...');
      const result = await createUserWithEmailAndPassword(auth, email, password);
      console.log('Signup successful:', result.user.uid);
      
      // Create initial user profile in Firestore
      await setDoc(doc(db, 'users', result.user.uid), {
        username: userId,
        bio: '',
        userId: result.user.uid,
        photoURL: result.user.photoURL || '',
        socialLinks: {},
        createdAt: new Date().toISOString(),
        role: 'user',
        isBanned: false,
        isAdmin: false
      });
      
      console.log('Created user profile in Firestore');
    } catch (error: any) {
      console.error('Signup error:', error);
      throw error;
    }
  }

  async function login(email: string, password: string) {
    try {
      console.log('Attempting login...');
      const result = await signInWithEmailAndPassword(auth, email, password);
      console.log('Login successful:', result.user.uid);
      
      // Check if user is banned (this is also handled by the auth state listener,
      // but we check here to provide immediate feedback during login)
      const userDoc = await getDoc(doc(db, 'users', result.user.uid));
      if (userDoc.exists() && userDoc.data().isBanned) {
        console.log('User is banned, signing out');
        await signOut(auth);
        throw new Error('Your account has been banned. Please contact support for assistance.');
      }
    } catch (error: any) {
      console.error('Login error:', error);
      throw error;
    }
  }

  async function logout() {
    try {
      console.log('Attempting logout...');
      await signOut(auth);
      console.log('Logout successful');
    } catch (error: any) {
      console.error('Logout error:', error);
      throw error;
    }
  }

  async function resetPassword(email: string) {
    try {
      console.log('Attempting password reset...');
      
      // Add actionCodeSettings to customize the password reset email
      const actionCodeSettings = {
        url: `${window.location.origin}/login`,
        handleCodeInApp: true
      };
      
      await sendPasswordResetEmail(auth, email, actionCodeSettings);
      console.log('Password reset email sent');
    } catch (error: any) {
      console.error('Password reset error:', error);
      throw error;
    }
  }

  const reauthenticate = async (password: string) => {
    if (!user || !user.email) throw new Error('No user logged in');
    
    try {
      const credential = EmailAuthProvider.credential(user.email, password);
      await reauthenticateWithCredential(user, credential);
      return true;
    } catch (error: any) {
      throw new Error('Invalid password');
    }
  };

  const updateEmail = async (newEmail: string, password: string) => {
    if (!user) throw new Error('No user logged in');
    
    try {
      // First reauthenticate
      await reauthenticate(password);
      
      // Then update email with action code settings
      const actionCodeSettings = {
        url: `${window.location.origin}/profile`,
        handleCodeInApp: true
      };
      
      // Update email in Firebase Auth
      await verifyBeforeUpdateEmail(user, newEmail, actionCodeSettings);
      
      // Update email in Firestore user document
      const userRef = doc(db, 'users', user.uid);
      await setDoc(userRef, {
        email: newEmail,
        isAdmin: false  // Preserve admin status
      }, { merge: true });
      
      return true;
    } catch (error: any) {
      throw error;
    }
  };

  async function verifyEmail() {
    if (!user) throw new Error('No user logged in');
    
    try {
      await user.sendEmailVerification();
      console.log('Verification email sent');
    } catch (error: any) {
      console.error('Error sending verification email:', error);
      throw error;
    }
  }

  const value = {
    user,
    loading,
    isBanned,
    isAdmin,
    signup,
    login,
    logout,
    resetPassword,
    updateEmail,
    verifyEmail
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}